import {
  PREDICATE_PROPERTY_OPERATOR,
  PREDICATE_TYPE,
  isPolygonPredicateType
} from "constants/filter.constants";
import { uid } from "utils";

import { IPredicateModel, IQuery } from "models/filter";
import { isShapefileBoundaryProperty } from "models/groupBy";

import {
  ShapefileBufferLabelUnit,
  shapefileBoundaryPredicateLabel
} from "components/filter/util/shapefileFilterPredicate";

// The key the server reports the whole filter's count under, which is what the
// branch at the top of the tree shows.
const ACTIVE_COUNTS_KEY: string = "Active";

/**
 * Get the predicate model representation of a given query.
 *
 * @param {IQuery} query the query to get the predicate models for.
 * @param {[]} counts The list of counts for the predicates.
 * @param {Maybe<Map<string, string>>} propertyTitleLookup Optional map of property → display title.
 *   Used to surface clean column titles for predicates that lack a stored groupByTitle (e.g. old
 *   saved filters). Threaded recursively into children_nodes. Falls back to raw split when absent.
 * @param {ShapefileBufferLabelUnit} bufferLabelUnit The unit a shapefile buffer is spelled in,
 *   which is the reader's own — the component holding the tree reads it off the unit converter.
 *   Miles when nobody says, which is the text this built before it was asked.
 * @returns {IPredicateModel} The predicate model representation of the query.
 */
const queryToPredicates = (
  query: IQuery,
  counts: [],
  propertyTitleLookup?: Maybe<Map<string, string>>,
  bufferLabelUnit?: ShapefileBufferLabelUnit
): IPredicateModel => {
  if (!query) return;

  const predicateChildren = query.predicates
    .filter((pred) => pred && pred.type)
    .reduce((result, pred) => {
      let title = "Polygon";
      let displayTitle = "";
      const op =
        pred.operator === PREDICATE_PROPERTY_OPERATOR.In
          ? PREDICATE_PROPERTY_OPERATOR.Equals
          : pred.operator === PREDICATE_PROPERTY_OPERATOR.NotIn
          ? PREDICATE_PROPERTY_OPERATOR.NotEqual
          : pred.operator;

      if (pred.type === PREDICATE_TYPE.Property) {
        if (pred.property === undefined || pred.value === undefined) {
          // skip predicate as its missing the property value.
          return result;
        }
        if (isShapefileBoundaryProperty(pred.property)) {
          // The shapefile filtering toggle's predicate reads as the shapefile
          // and the way it matches, not as a property compared to a value: the
          // reserved column name behind it must never reach the screen, and the
          // value it holds is the shapefile's own name, so the usual
          // "<field> = <value>" would say that name twice.
          title = shapefileBoundaryPredicateLabel(pred, bufferLabelUnit);
          displayTitle = title;
        } else {
          // Fallback chain: group/radius/shapefile-qualified partialContextTitle
          // (set at predicate-creation time from getColumnLabelWithContext) →
          // stored groupByTitle → lookup by property → raw last-segment split.
          // Never mutates pred.property — display-only (FR6/C1).
          const leafTitle =
            pred.partialContextTitle ||
            pred.groupByTitle ||
            propertyTitleLookup?.get(pred.property) ||
            pred.property.split(".").at(-1);
          if (pred.rollup) {
            title = `${leafTitle} ${op} ${pred.rollup}`;
            displayTitle = `${op} ${pred.rollup}`;
          } else if (pred.value[0] === "" || pred.value === "") {
            title = `${leafTitle} ${op} n/a`;
            displayTitle = ` ${op} n/a`;
          } else {
            title = `${leafTitle} ${op} ${pred.value}`;
            if (Array.isArray(pred.value)) {
              const formattedPredValue = pred.value.join(" - ");
              displayTitle = `${op} ${pred.sourceKey ?? formattedPredValue}`;
            } else {
              // For scalar-value predicates, set displayTitle to operator + value so the
              // full clean field name is shown via name/tooltip without lossy slice(1).
              displayTitle = `${op} ${pred.value}`;
            }
          }
        }
      } else if (isPolygonPredicateType(pred.type)) {
        // A MultiPolygon is a Polygon selection that was refined into disjoint
        // pieces by intersecting lassos — display it the same as a Polygon.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        if (pred.source && pred.source === "selection") {
          title = `Polygon ${pred.operator} Selection`;
        } else {
          title = "Polygon = Map View";
        }
        displayTitle = title;
      } else if (pred.type === PREDICATE_TYPE.WellList) {
        title = `Well List ${op} ${pred.value.length} wells`;
        displayTitle = `Well List`;
      }

      const id = pred.id;
      let count = 0;
      if (id in counts) {
        count = counts[id];
      }
      if (pred.type in counts) {
        //for polygon/well list counts
        count = counts[pred.type];
      } else if (
        pred.type === PREDICATE_TYPE.MultiPolygon &&
        PREDICATE_TYPE.Polygon in counts
      ) {
        // The backend reports a MultiPolygon selection's count under "Polygon".
        count = counts[PREDICATE_TYPE.Polygon];
      }
      if (pred.type === PREDICATE_TYPE.WellList && ACTIVE_COUNTS_KEY in counts) {
        // The list's own count is how many wells the user picked, which reads
        // as a contradiction next to the smaller count on the branch holding
        // it once the map view has narrowed the answer. Show what the branch
        // shows, so the two agree.
        count = counts[ACTIVE_COUNTS_KEY];
      }
      const child: IPredicateModel = {
        id,
        title,
        displayTitle,
        name: title,
        expanded: true,
        operator: pred.operator,
        children: [],
        count,
        type: "",
        isChecked: false,
        actualValue: pred
      };

      result.push(child);
      return result;
    }, []);

  const children: IPredicateModel[] = query.children_nodes.map((node) =>
    queryToPredicates(node, counts, propertyTitleLookup, bufferLabelUnit)
  );
  let branchCount = 0;

  const allChildren = predicateChildren.concat(children);

  let countsKey = ACTIVE_COUNTS_KEY;
  let formattedRootName = countsKey;
  if (query?.predicates?.length > 0 && query?.predicates[0]?.property) {
    // TODO: REMOVE PROPERTY NAME SPLITTING
    const splitted = query.predicates[0].property.split(".");
    countsKey = query.predicates[0].property;
    const rootSplit = splitted[splitted.length - 1].split("_");

    // rootSplit.length === 6 mean we're processing geom bin filter
    const propIndex = rootSplit.length === 6 ? 3 : 0;

    const formattedProp = rootSplit[propIndex]
      .split(/(?=[A-Z][a-z])/)
      .join(" ")
      .replace("Geom", "Polygon");

    formattedRootName = formattedProp;
    if (splitted.length === 3) {
      const propertyName = splitted[1];
      formattedRootName = ` ${propertyName}: ${formattedRootName}`;
    }
    // Prefer the field's clean display name for the branch label. partialContextTitle
    // is only populated for some groups (Forecast/Performance/etc.) and is empty for
    // e.g. Well Design fields, so fall back to groupByTitle, then the property->title
    // lookup (covers old saved filters), before keeping the raw property split.
    // A shapefile filtering branch names itself the same way its one predicate
    // does, so the tree and the chip read alike.
    const rootDisplayName = isShapefileBoundaryProperty(query.predicates[0].property)
      ? shapefileBoundaryPredicateLabel(query.predicates[0], bufferLabelUnit)
      : query.predicates[0].partialContextTitle ||
        query.predicates[0].groupByTitle ||
        propertyTitleLookup?.get(query.predicates[0].property);
    if (rootDisplayName) {
      formattedRootName = rootDisplayName;
    }
  }
  if (countsKey in counts) {
    branchCount = counts[countsKey];
  }
  const root: IPredicateModel = {
    id: uid(),
    title: query.operator,
    displayTitle: "",
    name: formattedRootName,
    expanded: true,
    count: branchCount,
    operator: query.operator,
    children: [],
    type: "",
    isChecked: false,
    actualValue: query
  };
  root.children = allChildren;
  return root;
};

export default queryToPredicates;
